import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { syncAllUsersFollowCounts } from '../services/followService';

const AdminPanel: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [syncStats, setSyncStats] = useState<{ success: number; failed: number } | null>(null);

  // Lista de IDs de administradores
  const adminIds = ['admin1Id', 'admin2Id']; // Reemplazar con IDs reales de administradores

  // Verificar si el usuario actual es administrador
  useEffect(() => {
    if (currentUser) {
      const userIsAdmin = adminIds.includes(currentUser.uid);
      setIsAdmin(userIsAdmin);
      
      // Redirigir si no es administrador
      if (!userIsAdmin) {
        setMessage({ text: 'No tienes permisos para acceder a esta página', type: 'error' });
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } else {
      // Redirigir si no hay usuario autenticado
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Función para sincronizar contadores de seguidores
  const handleSyncFollowCounts = async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setMessage({ text: 'Sincronizando contadores de seguidores...', type: 'info' });
    
    try {
      const result = await syncAllUsersFollowCounts();
      setSyncStats(result);
      setMessage({ 
        text: `Sincronización completada: ${result.success} usuarios actualizados, ${result.failed} fallidos`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error al sincronizar contadores:', error);
      setMessage({ 
        text: 'Error al sincronizar contadores. Consulta la consola para más detalles.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md mt-8">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Panel de Administración</h1>
        {message && (
          <div className={`p-4 rounded-md mb-4 ${
            message.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
            message.type === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
            'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
          }`}>
            {message.text}
          </div>
        )}
        <p className="text-gray-600 dark:text-gray-400">Redirigiendo al inicio...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md mt-8">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Panel de Administración</h1>
      
      {message && (
        <div className={`p-4 rounded-md mb-4 ${
          message.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
          message.type === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
          'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
        }`}>
          {message.text}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-3">Sincronización de Seguidores</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Sincroniza los contadores de seguidores y seguidos para todos los usuarios con los datos reales de las subcollections.
          </p>
          
          {syncStats && (
            <div className="mb-4 p-3 bg-gray-200 dark:bg-gray-600 rounded-md">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Usuarios actualizados:</span> {syncStats.success}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Usuarios fallidos:</span> {syncStats.failed}
              </p>
            </div>
          )}
          
          <button
            onClick={handleSyncFollowCounts}
            disabled={loading}
            className="px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Procesando...
              </>
            ) : 'Sincronizar Contadores'}
          </button>
        </div>
        
        {/* Aquí puedes añadir más secciones para otras funciones administrativas */}
      </div>
    </div>
  );
};

export default AdminPanel;
